import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Stack, Tooltip } from '@mui/material'

import { Input, Switch } from '@repo/ui'
import {
  WEBSITE_DISPLAY_CLAUSE_WARNING,
  WEBSITE_DISPLAY_REASON_TEXT,
  WEBSITE_DISPLAY_SURFACE_FIELD,
  WEBSITE_LOGO_PRIORITY_MAX,
  WEBSITE_LOGO_PRIORITY_MIN,
  checkWebsiteDisplayToggle,
  publicityClauseStatus,
  websiteDisplayEligibility,
  type WebsiteDisplayInput,
  type WebsiteDisplaySurface,
} from '@repo/types'

import { ConfirmSettingModal } from 'components'
import { useSnackbar } from 'contexts'
import { useSetCompanyWebsiteDisplay, useWebsiteDisplayStatus } from 'hooks/api'
import type { WebsiteDisplayPatch } from 'services'
import type { Company } from 'types'
import { date, getErrorMessage } from 'utils'

/**
 * The website-display columns on Internal → Companies (API migration 0142):
 *
 *   Show on website · clients    the website's clients page
 *   Show on website · homepage   the homepage hero's logo marquee
 *   Show on website · industry   the industry pages matching the company's categories
 *   Website priority             curation: lower = more important, blank = unranked
 *   Publicity opt-out            the brand withdrew permission in writing
 *
 * The rule is `checkWebsiteDisplayToggle` in @repo/types, the same function the API
 * enforces, for all three switches alike:
 *   - turning a switch ON is BLOCKED (switch disabled, reason in the tooltip) for an
 *     opted-out company and for a demo or internal company;
 *   - turning one ON when no member has accepted the Terms version with the publicity
 *     clause asks the staffer to confirm written permission first. The API refuses the
 *     write without that confirmation and STORES who confirmed and when, which the
 *     switch tooltips show;
 *   - recording an opt-out turns all three switches off and clears that evidence.
 *
 * Trialing companies are not excluded (decided, Spencer 2026-10-09): the switches are
 * the only gate.
 *
 * Writes are platform-admin only (§27: visible + disabled + tooltip for everyone else).
 */

const SURFACE_LABEL: Record<WebsiteDisplaySurface, string> = {
  clients: 'the website clients page',
  homepage: 'the website homepage',
  industry: 'the website industry pages for its categories',
}

type Pending =
  | { kind: 'enable'; company: Company; surface: WebsiteDisplaySurface }
  | { kind: 'opt_out'; company: Company; value: boolean }

interface Row {
  company: Company
}

const asDraft = (value: number | null) => (value === null ? '' : String(value))

/**
 * Inline priority editor. Commits on blur or Enter, reverts on Escape or an invalid
 * value; blank clears the priority. Local state so typing does not write per keystroke.
 */
const PriorityCell = ({
  company,
  disabled,
  tooltip,
  onCommit,
}: {
  company: Company
  disabled: boolean
  tooltip: string
  onCommit: (value: number | null) => void
}) => {
  const stored = company.website_logo_priority ?? null
  const [draft, setDraft] = useState(asDraft(stored))
  useEffect(() => setDraft(asDraft(stored)), [stored])

  const commit = () => {
    const trimmed = draft.trim()
    const next = trimmed === '' ? null : Number(trimmed)
    const valid =
      next === null ||
      (Number.isInteger(next) && next >= WEBSITE_LOGO_PRIORITY_MIN && next <= WEBSITE_LOGO_PRIORITY_MAX)
    if (!valid) {
      setDraft(asDraft(stored))
      return
    }
    if (next !== stored) onCommit(next)
  }

  return (
    <Tooltip title={tooltip}>
      <span onClick={(e) => e.stopPropagation()} style={{ width: 88 }}>
        <Input
          type="number"
          value={draft}
          placeholder="—"
          disabled={disabled}
          onChange={setDraft}
          onBlur={commit}
          handleKeyDown={(e) => {
            // The grid uses arrow keys and Enter for cell navigation; keep them here.
            e.stopPropagation()
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            if (e.key === 'Escape') setDraft(asDraft(stored))
          }}
          inputProps={{
            min: WEBSITE_LOGO_PRIORITY_MIN,
            max: WEBSITE_LOGO_PRIORITY_MAX,
            step: 1,
            'aria-label': `Website priority: ${company.name}`,
          }}
        />
      </span>
    </Tooltip>
  )
}

export const useWebsiteDisplayColumns = ({ canEdit }: { canEdit: boolean }) => {
  const { open: notification } = useSnackbar()
  const { data: status } = useWebsiteDisplayStatus()
  const setDisplay = useSetCompanyWebsiteDisplay()
  const [pending, setPending] = useState<Pending | null>(null)

  const clauseVersion = status?.clauseVersion
  const isSaving = setDisplay.isPending
  const { mutate } = setDisplay
  // Absent from the map, or the map still loading, reads as "not recorded": the safe
  // direction, since it asks for confirmation rather than skipping it.
  const memberVersion = useCallback((c: Company) => status?.memberTermsVersions?.[c.id] ?? null, [status])
  const confirmerEmail = useCallback(
    (c: Company) =>
      c.website_permission_confirmed_by
        ? status?.permissionConfirmers?.[c.website_permission_confirmed_by] ?? 'a staffer no longer on record'
        : null,
    [status],
  )

  const save = useCallback(
    (companyId: string, patch: WebsiteDisplayPatch) =>
      mutate(
        { companyId, patch },
        {
          onSuccess: () => setPending(null),
          onError: (error) => notification({ type: 'error', title: getErrorMessage(error) }),
        },
      ),
    [mutate, notification],
  )

  // Memoised so the grid's column defs stay referentially stable between renders.
  const columns = useMemo(() => {
    const toInput = (c: Company): WebsiteDisplayInput => ({
      show_on_website_clients: !!c.show_on_website_clients,
      show_on_website_homepage: !!c.show_on_website_homepage,
      show_on_website_industry: !!c.show_on_website_industry,
      publicity_opt_out: !!c.publicity_opt_out,
      kind: c.kind ?? 'standard',
      archived: !!c.archived,
      suspended: !!c.suspended,
      latest_member_terms_version: memberVersion(c),
    })

    const onSurfaceChange = (company: Company, surface: WebsiteDisplaySurface, next: boolean) => {
      const field = WEBSITE_DISPLAY_SURFACE_FIELD[surface]
      if (!next) return save(company.id, { [field]: false })
      const check = checkWebsiteDisplayToggle(toInput(company))
      if ('reason' in check) return
      if (check.warning) return setPending({ kind: 'enable', company, surface })
      save(company.id, { [field]: true })
    }

    const evidenceLine = (company: Company): string => {
      const who = confirmerEmail(company)
      if (!who && !company.website_permission_confirmed_at) return 'No written-permission confirmation is recorded.'
      const when = company.website_permission_confirmed_at ? ` on ${date(company.website_permission_confirmed_at)}` : ''
      return `Written permission confirmed by ${who ?? 'a staffer no longer on record'}${when}.`
    }

    const surfaceTooltip = (company: Company, surface: WebsiteDisplaySurface): string => {
      if (!canEdit) return 'Only a Vera admin can change website display.'
      const input = toInput(company)
      const check = checkWebsiteDisplayToggle(input)
      const on = !!company[WEBSITE_DISPLAY_SURFACE_FIELD[surface]]
      if (!on && 'reason' in check) return WEBSITE_DISPLAY_REASON_TEXT[check.reason]
      if (!on) return `Off. Not shown on ${SURFACE_LABEL[surface]}.`
      const verdict = websiteDisplayEligibility(input, surface)
      if (!verdict.eligible) return `On, but hidden: ${WEBSITE_DISPLAY_REASON_TEXT[verdict.reason]}`
      return verdict.basis === 'terms'
        ? `On. A member accepted the Terms with the publicity clause (version ${memberVersion(company)}).`
        : `On, on written permission. No member has accepted the Terms version with the publicity clause. ${evidenceLine(company)}`
    }

    const surfaceColumn = (surface: WebsiteDisplaySurface, headerName: string) => ({
      field: WEBSITE_DISPLAY_SURFACE_FIELD[surface],
      headerName,
      type: 'custom',
      width: 220,
      align: 'center',
      headerAlign: 'center',
      // Not server-sortable (buildSortOrder supports name/country/created_at only).
      sortable: false,
      renderCell: ({ row }: { row: Row }) => {
        const c = row.company
        const on = !!c[WEBSITE_DISPLAY_SURFACE_FIELD[surface]]
        const check = checkWebsiteDisplayToggle(toInput(c))
        // Turning OFF is always allowed; only turning ON can be blocked.
        const blocked = !on && 'reason' in check
        return (
          <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
            <Tooltip title={surfaceTooltip(c, surface)}>
              <span onClick={(e) => e.stopPropagation()}>
                <Switch
                  checked={on}
                  disabled={!canEdit || blocked || isSaving}
                  inputProps={{ 'aria-label': `${headerName}: ${c.name}` }}
                  onChange={(_: unknown, next: boolean) => onSurfaceChange(c, surface, next)}
                />
              </span>
            </Tooltip>
          </Stack>
        )
      },
    })

    return [
      surfaceColumn('clients', 'Show on website · clients'),
      surfaceColumn('homepage', 'Show on website · homepage'),
      surfaceColumn('industry', 'Show on website · industry'),
      {
        field: 'website_logo_priority',
        headerName: 'Website priority',
        type: 'custom',
        width: 150,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: ({ row }: { row: Row }) => (
          <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
            <PriorityCell
              company={row.company}
              disabled={!canEdit || isSaving}
              tooltip={
                canEdit
                  ? `Lower shows first on every website list (${WEBSITE_LOGO_PRIORITY_MIN} to ${WEBSITE_LOGO_PRIORITY_MAX}). Blank = unranked, shown after ranked logos. The homepage and industry pages show only the top few.`
                  : 'Only a Vera admin can change the website priority.'
              }
              onCommit={(value) => save(row.company.id, { website_logo_priority: value })}
            />
          </Stack>
        ),
      },
      {
        field: 'publicity_opt_out',
        headerName: 'Publicity opt-out',
        type: 'custom',
        width: 170,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: ({ row }: { row: Row }) => {
          const c = row.company
          const optedOut = !!c.publicity_opt_out
          const title = !canEdit
            ? 'Only a Vera admin can record a publicity opt-out.'
            : optedOut
              ? `Opted out${c.publicity_opt_out_at ? ` on ${date(c.publicity_opt_out_at)}` : ''}. The website does not show this company.`
              : 'No opt-out recorded. Turn on when the company withdraws permission in writing.'
          return (
            <Stack width="100%" height="100%" alignItems="center" justifyContent="center">
              <Tooltip title={title}>
                <span onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={optedOut}
                    disabled={!canEdit || isSaving}
                    inputProps={{ 'aria-label': `Publicity opt-out: ${c.name}` }}
                    onChange={(_: unknown, next: boolean) => setPending({ kind: 'opt_out', company: c, value: next })}
                  />
                </span>
              </Tooltip>
            </Stack>
          )
        },
      },
    ]
  }, [canEdit, isSaving, memberVersion, confirmerEmail, save])

  const modal = (() => {
    if (!pending) return null
    const name = pending.company.name
    if (pending.kind === 'enable') {
      const field = WEBSITE_DISPLAY_SURFACE_FIELD[pending.surface]
      return (
        <ConfirmSettingModal
          open
          onClose={() => setPending(null)}
          onConfirm={() => save(pending.company.id, { [field]: true, confirm_written_permission: true })}
          title={`Show ${name} on ${SURFACE_LABEL[pending.surface]}?`}
          preamble={
            publicityClauseStatus(memberVersion(pending.company)) === 'not_recorded'
              ? `${WEBSITE_DISPLAY_CLAUSE_WARNING}${clauseVersion ? ` (Terms version ${clauseVersion} or later.)` : ''}`
              : undefined
          }
          consequence={`Confirm that ${name} has given Vera written permission to use its name and logo. Your name and the time are recorded as the confirmation, and Vera's website can then show the logo on ${SURFACE_LABEL[pending.surface]}.`}
          confirmLabel="Confirm permission and show"
          loading={isSaving}
        />
      )
    }
    return pending.value ? (
      <ConfirmSettingModal
        open
        onClose={() => setPending(null)}
        onConfirm={() => save(pending.company.id, { publicity_opt_out: true })}
        title={`Record ${name}'s publicity opt-out?`}
        consequence={`All three website switches turn off, the recorded permission confirmation is cleared, and the website stops showing ${name}'s name and logo. Vera also has to stop new uses in other marketing within a reasonable time.`}
        confirmLabel="Record opt-out"
        destructive
        loading={isSaving}
      />
    ) : (
      <ConfirmSettingModal
        open
        onClose={() => setPending(null)}
        onConfirm={() => save(pending.company.id, { publicity_opt_out: false })}
        title={`Clear ${name}'s publicity opt-out?`}
        consequence={`Clear it only if ${name} has given permission again in writing. The website switches stay off until someone turns them on.`}
        confirmLabel="Clear opt-out"
        loading={isSaving}
      />
    )
  })()

  return { columns, modal }
}
